/* eslint-disable  @typescript-eslint/no-explicit-any */
import {FacetsV2Service, type FilterNodeRequestUnique} from '../facets/facets-v2.service';
import type { SearchTab } from '../../../components/result-page/search-tab/search-tab';
import { middlewareSearchTabAdapater } from '../../middleware-pipe/middleware-search-tab.adapater';
import type { SearchApi } from '../../../api/model/response/ApiResponse';
import {BehaviorSubject, filter, Observable, of, switchMap, take} from 'rxjs';
import {cloneObjectWithNoReference} from '../../helpers/clone-object-with-no-reference';
import type {FilterRequest} from '../../../models/cose-api/request/query-body-request.model';
import mapFacetNames from '../../../helpers/mapFacetNames-v2';

// eslint-disable-next-line @stencil-community/ban-exported-const-enums
export enum QueryContext {
  mobileFilteringShowResults = 'mobileFilteringShowResults',
  ignored = 'ignored'
}

export class CoseRequestService {
  facetsV2Service: FacetsV2Service;
  context: SearchTab;
  readonly ESCAPE_MIDDLEWARE_NAME: string = 'EscapeMiddleware';
  readonly CUT_FACETS_REQUEST_MIDDLEWARE_NAME: string = 'CutFacetsRequestMiddleware';

  currentResponse$: BehaviorSubject<any> = new BehaviorSubject(null);
  onQueryContext$: BehaviorSubject<QueryContext | null> = new BehaviorSubject(QueryContext.ignored);

  constructor(facetsV2Service: FacetsV2Service) {
      this.facetsV2Service = facetsV2Service;
  }

  get currentResponse(): Observable<any> {
      return this.currentResponse$.pipe(filter((value => value !== null)));
  }

  get onQueryContext(): Observable<QueryContext> {
      return this.onQueryContext$.asObservable();
  }

  setOnQueryContext(queryContext = QueryContext.ignored) {
      this.onQueryContext$.next(queryContext);
  }

  initDefaultPipe(): void {
      this.context.checkExistAndQueryCallMethod();
  }

  initPipe(dividerMobileFacets: string[]): Observable<SearchApi> {
      return this.initPipeWithCustomMiddleware(dividerMobileFacets);
  }

  initPipeWithCustomMiddleware(dividerMobileFacets: string[]): Observable<SearchApi> {
      return of(this.registerCutFacetsRequestMiddleware(dividerMobileFacets))
        .pipe(take(1),
          switchMap(() => {
              let customMiddlewares = this.context.middlewares;
              customMiddlewares = [
                ...customMiddlewares.split('|').slice(0, customMiddlewares.split('|').length - 1),
                this.CUT_FACETS_REQUEST_MIDDLEWARE_NAME,
                ...customMiddlewares.split('|').slice(customMiddlewares.split('|').length - 1),
              ].join('|');
              return middlewareSearchTabAdapater<SearchApi>(
                this.context,
                {customMiddlewares},
                {skipAutocorrection: true, sort: this.context.getSortingOption()},
              ).pipe(take(1));
          })
        );
  }

  initPipeWithEscape(customMiddlewares: string): Observable<SearchApi> {
      return middlewareSearchTabAdapater<SearchApi>(
        this.context,
        {customMiddlewares, updateOnlyMobileStates: true},
        {forcedLimit: 1, skipAutocorrection: true}
      ).pipe(take(1));
  }

  initEscapePipe() {
      this.facetsV2Service.facetsMobileService.facetsRequestState
        .pipe(
          take(1),
          switchMap(() => this.context.getMiddlewareRegistry()),
          switchMap(() => this.registerMiddleware()),
        )
        .subscribe(() => {
            let customMiddlewares = this.context.middlewares;
            customMiddlewares = [
              ...customMiddlewares.split('|').slice(0, customMiddlewares.split('|').length - 1),
              this.ESCAPE_MIDDLEWARE_NAME,
              ...customMiddlewares.split('|').slice(-1),
            ].join('|');
            // @ts-ignore
            this.initPipeWithEscape(customMiddlewares).pipe(take(1))
              .subscribe(
                {
                  error: (err) => {
                  }
                }
              );
        });
  }

  async registerMiddleware() {
      return await this.context.registerMiddleware(
        this.ESCAPE_MIDDLEWARE_NAME,
        async (details, next, middlewareDepth, middlewareArray) => {
            const response = await next(details);
            response.facetsOptions['facets'] = mapFacetNames(details.context, response.facetsOptions['facets']);
            this.currentResponse$.next(response);
            // @ts-ignore
            throw new Error('You escape from PipeMiddleware');

        }
      );
  }

  async registerCutFacetsRequestMiddleware(dividerMobileFacets: string[]) {
      return await this.context.registerMiddleware(
        this.CUT_FACETS_REQUEST_MIDDLEWARE_NAME,
        async (details, next, middlewareDepth, middlewareArray) => {

            const {clearedQueryFilters, clearChange}
              = CoseRequestService.clearWhenDividerFacest(details.middlewareRequest.requestBody.filters, dividerMobileFacets);

            if(clearChange) {
              const filtersWithUnique = FacetsV2Service.setUnique(cloneObjectWithNoReference(clearedQueryFilters));
              this.facetsV2Service.setFacetsState(filtersWithUnique);
              this.facetsV2Service.setFacetsRequestState(filtersWithUnique);
              details.middlewareRequest.requestBody.filters = clearedQueryFilters;
            }

            const response = await next(details);
            return {...response};
        }
      );
  }

  static getSetInstance(facetsV2Service: FacetsV2Service) {
      const instance = new this(facetsV2Service);
      return instance;
  }

  static clearWhenDividerFacest(queryFilters: FilterRequest[] | FilterNodeRequestUnique[], dividerMobileFacets: string[]): {clearedQueryFilters: FilterRequest[] | FilterNodeRequestUnique[], clearChange: boolean} {
      if(!queryFilters || (queryFilters && queryFilters.length === 0)) {
        return {clearedQueryFilters: [], clearChange: false};
      }

      let clearedQueryFilters = cloneObjectWithNoReference(queryFilters);
      let clearChange = false;

      if (queryFilters && queryFilters.length > 0) {
        dividerMobileFacets.forEach((id) => {
            clearedQueryFilters = clearedQueryFilters.map((facet, i) => {
                if (facet && facet?.id && facet.id === id) {
                  const canFacetChidrenClearAll: boolean[] = [];
                  facet.children.forEach((filter, j) => {
                      const isFilterChildrenEmpty = filter.children.length === 0;

                      canFacetChidrenClearAll.push(isFilterChildrenEmpty);

                      if (isFilterChildrenEmpty) {
                        delete clearedQueryFilters[i].children[j];
                      }
                  });

                  clearedQueryFilters[i].children = clearedQueryFilters[i].children.filter(value => !!value);

                  if (!canFacetChidrenClearAll.includes(false)) {
                    facet.children = [];
                    clearChange = true;
                  }
                }
                return facet && facet?.children && facet.children.length === 0 ? undefined : facet;
            });
        });

        clearedQueryFilters = clearedQueryFilters.filter(facet => !!facet);
      }

      return {clearedQueryFilters, clearChange};
  }
}
